import { css } from '@emotion/css';
import { tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Flex, Input, Tabs, theme } from 'antd';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { LabResultAttachment } from '../../../../generated/openapi/befunde-api';
import { useKeyAction } from '../../../../hooks/useKeyAction.ts';
import { useLabResultViewStore } from '../../../../store/useLabResultViewStore.ts';

const useObservationResultFilterClassNames = () => {
  const { token } = theme.useToken();

  return {
    layoutClassName: css`
      flex-wrap: wrap;
      width: 100%;
      gap: 0 ${token.paddingLG}px;
      justify-items: right;
      margin-top: ${token.margin}px;
    `,
    inputClassName: css`
      margin: 8px 8px 0 0;
      min-width: 74px;
    `,
    tabsClassName: css`
      flex-grow: 1;
      overflow: hidden;
    `,
  };
};

/*
  key:
    #<none> -> if no specimen, value, or attachment available
    #all
      -> Tab 'Probe', if specimen but no values available
      -> Tab 'Alle Werte', if values (and optional specimen) available
    #patho
      -> Tab 'pathologisch' if patho values available
    #non-patho
      -> Tab 'nicht pathologisch' if patho and non-patho values available
    #attach-<index>
      -> Tab '<filename>' for each attachment
 */

function defaultKey(hasSpecimen: boolean, values: number, attachments: LabResultAttachment[]) {
  if (hasSpecimen || values > 0) {
    return 'all';
  }
  return attachments?.length > 0 ? 'attach0' : '';
}

function getTabItems(hasSpecimen: boolean, values: number, pathological: number, attachments: LabResultAttachment[]) {
  const items = [];
  if (values > 0) {
    items.push({ key: 'all', label: `Alle Werte (${values})` });
    if (pathological > 0) {
      items.push({ key: 'patho', label: `pathologisch (${pathological})` });
      const nonPathoCount = values - pathological;
      if (nonPathoCount > 0) {
        items.push({ key: 'non-patho', label: `nicht pathologisch (${nonPathoCount})` });
      }
    }
  } else if (hasSpecimen) {
    items.push({ key: 'all', label: `Probe` });
  }
  if (attachments?.length > 0) {
    attachments.forEach((attachment, index) => {
      items.push({ key: `attach-${index}`, label: attachment.filename });
    });
  }
  return items;
}

export const LabResultTabAndFilter: React.FC<{
  hasSpecimen: boolean;
  values: number;
  pathological: number;
  attachments: LabResultAttachment[];
}> = ({ hasSpecimen, values, pathological, attachments }) => {
  const setTab = useLabResultViewStore(state => state.setTab);
  const tokens = useLabResultViewStore(state => state.tokens);
  const setTokens = useLabResultViewStore(state => state.setTokens);

  const [filterTokens, setFilterTokens] = useState(() => tokens.join(' '));
  const [focused, setFocused] = useState(false);
  const [key, setKey] = useState<string>(defaultKey(hasSpecimen, values, attachments));

  const { layoutClassName, inputClassName, tabsClassName } = useObservationResultFilterClassNames();

  useEffect(() => {
    setTokens(tokenize(filterTokens));
  }, [setTokens, filterTokens]);

  useEffect(() => {
    if (key.startsWith('attach-')) {
      setTab('ATTACHMENT', Number(key.substring(7)));
    } else {
      setTab('VALUE', key === 'all' ? 'ALL' : key === 'patho' ? 'PATHOLOGICAL' : 'NONE_PATHOLOGICAL');
    }
  }, [setTab, key]);

  const action = useMemo(() => {
    if (focused) {
      return () => {
        setFilterTokens('');
      };
    } else {
      return null;
    }
  }, [focused]);

  useKeyAction('Escape', action);

  const items = useMemo(
    () => getTabItems(hasSpecimen, values, pathological, attachments),
    [hasSpecimen, values, pathological, attachments]
  );

  if (items.length === 0) {
    return undefined;
  }

  return (
    <Flex className={layoutClassName}>
      <Tabs className={tabsClassName} size="small" activeKey={key} onChange={setKey} items={items} />
      {/* Hidden rather than disabled on attachment tabs: there is nothing to filter
          there, and a greyed-out field still suggests the feature applies. */}
      {values > 0 && !key.startsWith('attach-') && (
        <div className={inputClassName}>
          <Input
            placeholder="Filter"
            value={filterTokens}
            onChange={e => setFilterTokens(e.target.value)}
            allowClear
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
      )}
    </Flex>
  );
};
